import pluralizeVendor from 'pluralize'

const HTML_ESCAPE_MAP = Object.freeze({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
  '`': '&#96;'
})

const HTML_ESCAPE_REGEX = /[&<>"'`]/g
const CSS_COLOR_FUNCTION_NAMES = new Set(['rgb', 'rgba', 'hsl', 'hsla'])
const CSS_HEX_COLOR_LENGTHS = new Set([4, 5, 7, 9])
const MAX_CSS_COLOR_LENGTH = 64

export const escapeHtml = (value) =>
  String(value ?? '').replaceAll(HTML_ESCAPE_REGEX, (char) => HTML_ESCAPE_MAP[char])

export const escapeHtmlAttribute = (value) => escapeHtml(value)

const isAsciiLetter = (char) => {
  const code = char.codePointAt(0)
  return (code >= 65 && code <= 90) || (code >= 97 && code <= 122)
}

const isHexDigit = (char) => {
  const code = char.codePointAt(0)
  return (code >= 48 && code <= 57) || (code >= 65 && code <= 70) || (code >= 97 && code <= 102)
}

const isWhitespace = (char) => {
  const code = char.codePointAt(0)
  return code === 9 || code === 10 || code === 12 || code === 13 || code === 32
}

const isFunctionalCssColorChar = (char) => {
  return (
    isHexDigit(char) ||
    isWhitespace(char) ||
    char === '+' ||
    char === '-' ||
    char === '.' ||
    char === ',' ||
    char === '%' ||
    char === '/'
  )
}

const isHexCssColor = (value) => {
  if (!CSS_HEX_COLOR_LENGTHS.has(value.length) || value[0] !== '#') {
    return false
  }

  for (let index = 1; index < value.length; index += 1) {
    if (!isHexDigit(value[index])) {
      return false
    }
  }

  return true
}

const isNamedCssColor = (value) => {
  if (!value.length) {
    return false
  }

  for (const char of value) {
    if (!isAsciiLetter(char)) {
      return false
    }
  }

  return true
}

const isFunctionalCssColor = (value) => {
  const openingParenIndex = value.indexOf('(')
  if (openingParenIndex <= 0 || !value.endsWith(')')) {
    return false
  }

  const functionName = value.slice(0, openingParenIndex).toLowerCase()
  if (!CSS_COLOR_FUNCTION_NAMES.has(functionName)) {
    return false
  }

  const body = value.slice(openingParenIndex + 1, -1)
  if (!body.trim().length) {
    return false
  }

  for (const char of body) {
    if (!isFunctionalCssColorChar(char)) {
      return false
    }
  }

  return true
}

export const sanitizeCssColor = (value, fallback = '#000000') => {
  const normalizedValue = String(value ?? '').trim()
  if (!normalizedValue.length || normalizedValue.length > MAX_CSS_COLOR_LENGTH) {
    return fallback
  }

  if (
    isHexCssColor(normalizedValue) ||
    isNamedCssColor(normalizedValue) ||
    isFunctionalCssColor(normalizedValue)
  ) {
    return normalizedValue
  }

  return fallback
}

export const sanitizeHtmlId = (value, fallback = 'sanitized-id') => {
  const sanitizedValue = String(value ?? '').replaceAll(/[^A-Za-z0-9_.:-]+/g, '_')
  return sanitizedValue || fallback
}

/**
 * Pluralizes a given word based on the provided count.
 * Relies on a vendor function `pluralizeVendor` to perform the actual pluralization.
 *
 * @param {string} word - The word to be pluralized.
 * @param {number} count - The count determining whether the word should be singular or plural.
 * @returns {string} - The pluralized form of the word if count is not 1, otherwise the singular form.
 */
export const pluralize = (word, count) => {
  // Get the pluralized form from the vendor
  let pluralizedWord = pluralizeVendor(word, count)

  if (word !== pluralizedWord) {
    // Check for 'ES' ending
    if (pluralizedWord.endsWith('ES')) {
      pluralizedWord = pluralizedWord.slice(0, -2) + 'es'
    }
    // Check for 'S' ending (if not already handled by ES check)
    else if (pluralizedWord.endsWith('S')) {
      pluralizedWord = pluralizedWord.slice(0, -1) + 's'
    }
  }

  return pluralizedWord
}

/**
 * Converts a given string to camelCase.
 *
 * @param {string} text - The string to be converted to camelCase.
 * @returns {string} - The camelCase version of the input string.
 */
export const toCamelCase = (text) => {
  return (
    text
      // Trim the text
      .trim()
      // Replace sequences of spaces with a single space
      .replaceAll(/\s+/g, ' ')
      // Convert to camelCase
      .replaceAll(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match, index) {
        if (Number(match) === 0) return '' // or if (/\s+/.test(match)) for clarity
        return index === 0 ? match.toLowerCase() : match.toUpperCase()
      })
      .replaceAll(/\s/g, '')
  ) // Finally, remove any spaces left
}

/**
 * Converts a given camelCase string to "Title Case with spaces" (e.g., `employeeNumber` -> `Employee Number`).
 * If an array of strings is provided, it processes each string in the array.
 *
 * @param {string | string[]} input - The string or array of strings to be converted.
 * @returns {string | string[]} - The formatted string or array of formatted strings.
 */
export function toTitleWithSpaces(input) {
  // Helper function to convert camelCase to Title Case with spaces
  const formatToTitleCase = (str) => {
    return str
      .replaceAll(/([A-Z])/g, ' $1') // Add space before uppercase letters
      .replaceAll(/^./, (s) => s.toUpperCase()) // Capitalize the first letter
      .trim() // Remove leading/trailing spaces
  }

  if (Array.isArray(input)) {
    // Process each string in the array
    return input.map((item) => formatToTitleCase(item))
  }

  if (typeof input === 'string') {
    return formatToTitleCase(input)
  }

  throw new TypeError('Input must be a string or an array of strings')
}

/**
 * Function to highlight the searched text
 * @param text the complete text a part of which is to be highlighted
 * @param query the searched text that has to be highlighted
 * @return the final text with a highlighted part
 */
export const highlightText = (text, query) => {
  const safeText = String(text ?? '')
  const safeQuery = String(query ?? '')
  const matchedIndex = safeText.toLowerCase().indexOf(safeQuery.toLowerCase())

  if (safeQuery.length && matchedIndex !== -1) {
    const beforeMarked = escapeHtml(safeText.slice(0, matchedIndex))
    const marked = escapeHtml(safeText.slice(matchedIndex, matchedIndex + safeQuery.length))
    const afterMarked = escapeHtml(safeText.slice(matchedIndex + safeQuery.length))

    return `<span class="capitalize">${beforeMarked}<mark class="bg-primary-200 text-inherit">${marked}</mark>${afterMarked}</span>`
  }

  return `<span class="capitalize">${escapeHtml(safeText)}</span>`
}
